import { pmFetchJson } from "@penmate/react-shared/api/pmFetch";

export type UploadPhotoUrlResponse = { url: string; key: string };
export type UploadPhotoCompleteResponse = { img_path: string; url: string };
export type UploadProgress = { loaded: number; total: number; percent: number };

export async function createUploadPhotoUrl(args: { filename: string }): Promise<UploadPhotoUrlResponse> {
  return pmFetchJson<UploadPhotoUrlResponse>("/upload-photo-url", {
    method: "GET",
    params: { filename: args.filename },
  });
}

export async function uploadPhotoComplete(args: { key: string }): Promise<UploadPhotoCompleteResponse> {
  return pmFetchJson<UploadPhotoCompleteResponse>("/upload-photo-complete", {
    method: "GET",
    params: { key: args.key },
  });
}

export async function putFileToPresignedUrl(args: {
  url: string;
  file: File;
  onProgress?: (p: UploadProgress) => void;
}): Promise<void> {
  // Use XHR to support upload progress callbacks (fetch doesn't reliably expose upload progress).
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", args.url, true);
    if (args.file.type) xhr.setRequestHeader("Content-Type", args.file.type);

    xhr.upload.onprogress = (evt) => {
      if (!args.onProgress) return;
      const total = evt.lengthComputable ? evt.total : args.file.size || 0;
      const loaded = evt.loaded || 0;
      const percent = total > 0 ? Math.max(0, Math.min(100, Math.round((loaded / total) * 100))) : 0;
      args.onProgress({ loaded, total, percent });
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        args.onProgress?.({ loaded: args.file.size || 0, total: args.file.size || 0, percent: 100 });
        resolve();
      } else {
        reject(new Error(`Upload failed (${xhr.status}): ${xhr.responseText || xhr.statusText}`));
      }
    };
    xhr.onerror = () => reject(new Error("Upload failed (network error)."));
    xhr.onabort = () => reject(new Error("Upload canceled."));

    try {
      xhr.send(args.file);
    } catch (e) {
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}
